import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';

import { ReactComponent as SizeChangeIcon } from '../../assets/icons/table-field-allow-icon.svg';
import CustomInputSwitch from '../custom-input-switch/CustomInputSwitch';

interface TableFieldAllowInterFace {
  labelForHideColumn: {
    label: string;
    accessorKey: any;
    disableHideFunc: boolean;
  }[];
  hidedColumn: string[];
  setHidedColumn: React.Dispatch<React.SetStateAction<string[]>>;
  hoverLabel?: string;
}

/**
 * Table Field Allow function component
 * @returns
 */
function TableFieldAllow({
  labelForHideColumn,
  hidedColumn,
  setHidedColumn,
  hoverLabel,
}: TableFieldAllowInterFace) {
  const label: string = '';
  const reference = useRef<HTMLDivElement>(null);
  const buttonReference = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});

  /**
   * Function to update the dropdown position
   */
  const updatePosition = () => {
    if (!buttonReference.current) return;
    const rect = buttonReference.current.getBoundingClientRect();
    setStyle({
      top: rect.top + rect.height,
      left: rect.left + rect.width / 2,
      transform: 'translateX(-50%)',
    });
  };

  /**
   * Toggle the dropdown modal
   */
  const toggleModal = () => {
    setOpen((previous) => !previous);
  };

  /**
   * Handle the close th drop down function
   * @param event - Mouse click event
   */
  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    // Do nothing if clicking the button itself (the button handles the toggle)
    if (target.closest('.table-column-show-hide-button')) {
      return;
    }

    if (reference.current && !reference.current.contains(target)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true); // true = Use capture phase

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  /**
   * Function to Hide & Show all fields
   * @param type - Single or multi update
   * @param key - The column key or action (hideAll/showAll)
   */
  const hideAndShowFields = (type: 'single' | 'multi', key: string) => {
    if (type === 'multi') {
      if (key === 'hideAll') {
        setHidedColumn(
          labelForHideColumn
            .filter((eachLabel) => !eachLabel.disableHideFunc)
            .map((eachLabel) => eachLabel.accessorKey),
        );
      }

      if (key === 'showAll') {
        setHidedColumn([]);
      }
    } else {
      // If includes key then remove it else append the key to the existing array
      setHidedColumn((previous) => {
        if (previous.includes(key)) {
          return previous.filter((item) => item !== key);
        }
        return [...previous, key];
      });
    }
  };

  useEffect((): void => {
    document.addEventListener('mousedown', handleClickOutside);
  }, []);
  return (
    <div className="table-field-allow-warp">
      <div className="button-wrap">
        <button
          type="button"
          className="table-column-show-hide-button"
          onClick={toggleModal}
          ref={buttonReference}>
          <span className={`icon ${hidedColumn.length > 0 ? 'active' : ''}`}>
            <SizeChangeIcon />
          </span>
          {label && <span>{label}</span>}
        </button>
        {hoverLabel && <div className="hover-label">{hoverLabel}</div>}
      </div>
      {open &&
        ReactDOM.createPortal(
          <div className="table-field-allow-container" style={style} ref={reference}>
            <div className="table-field-allow-header">
              <div className="allow-all-field-button">
                <div
                  className="field-item"
                  onClick={() => hideAndShowFields('multi', 'hideAll')}
                  role="presentation">
                  Hide All
                </div>
                <div
                  className="field-item"
                  onClick={() => hideAndShowFields('multi', 'showAll')}
                  role="presentation">
                  Show All
                </div>
              </div>
            </div>
            <div className="allow-field-list">
              {labelForHideColumn
                .filter((eachLabel) => !eachLabel.disableHideFunc)
                .map((field: { label: string; accessorKey: any; disableHideFunc: boolean }) => (
                  <CustomInputSwitch
                    inputLabel={field.label}
                    value={!hidedColumn.includes(field.accessorKey)} // Switch button active if column is hided
                    name={field.accessorKey}
                    onChangeValue={(name: string) => hideAndShowFields('single', name)}
                    fontSize="small"
                  />
                ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
TableFieldAllow.defaultProps = {
  hoverLabel: '',
};
export default TableFieldAllow;
